import { memoizeGetCourseSectionTopic } from "./courses";
import { memoizeGetRoleplay } from "./roleplays";
import { memoizeGetBot } from "./bots";
import memoize from "memoizee";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";

const isBotExercise = (exercise) =>
  exercise === "bot" || exercise === "bot-listen-and-speak";

const memoizeGetUser = memoize(
  async (userId) => {
    try {
      const response = await getUser(userId, true);
      if (response.errorMessage) {
        throw new Error(response.errorMessage);
      }
      return response;
    } catch (error) {
      return null;
    }
  }, 
  { promise: true }
);

export const formatCompletion = async (completion) => {
  const {
    memberId,
    courseId,
    sectionId,
    topicId,
    roleplayId,
    botId,
    exercise,
  } = completion;
  try {
    const isIndependentExercise =
      courseId && sectionId && topicId
        ? courseId.includes("none-") ||
          sectionId.includes("none-") ||
          topicId.includes("none-")
        : false;

    let responses;
    if (courseId && sectionId && topicId && !isIndependentExercise) {
      responses = await Promise.all([
        memoizeGetUser(memberId),
        memoizeGetCourseSectionTopic(courseId, sectionId, topicId),
      ]);
    } else {
      responses = await Promise.all([memoizeGetUser(memberId), {}]);
    }

    const memberResponse = responses[0];
    const member = memberResponse.UserAttributes || {};
    const topicResponse = responses[1];
    const topic = topicResponse.Item || {};
    if (exercise === "roleplay") {
      const roleplayResponse = await memoizeGetRoleplay(roleplayId);
      const roleplay = roleplayResponse.Item || {};
      return {
        ...completion,
        preferred_username: member.preferred_username,
        topicName: !isIndependentExercise
          ? topic.topicName
          : "topic_name_not_known",
        roleplayName: roleplay.roleplayName || "roleplay_name_not_known",
      };
    }
    if (isBotExercise(exercise)) {
      const botResponse = await memoizeGetBot(botId);
      const bot = botResponse.Item || {};
      return {
        ...completion,
        preferred_username: member.preferred_username,
        topicName: !isIndependentExercise
          ? topic.topicName
          : "topic_name_not_known",
        botName: bot.botName || "bot_name_not_known",
      };
    }

    return {
      ...completion,
      preferred_username: member.preferred_username,
      topicName: topic.topicName || "topic_name_not_known",
    };
  } catch (error) {
    if (exercise === "roleplay") {
      return {
        ...completion,
        topicName: "topic_name_not_known",
        roleplayName: "roleplay_name_not_known",
      };
    }
    if (isBotExercise(exercise)) {
      return {
        ...completion,
        topicName: "topic_name_not_known",
        botName: "bot_name_not_known",
      };
    }

    return {
      ...completion,
      topicName: "topic_name_not_known",
    };
  }
};

export const formatCompletions = async (completions = []) =>
  Promise.all(completions.map(formatCompletion));
export const getAttemptPath = ({
  classroomId,
  courseId,
  sectionId,
  topicId,
  exercise,
  memberId,
  createdAt,
}) => {
  if (!courseId || !sectionId || !topicId || !exercise || !memberId || !createdAt) return null;
  const attempt = `${courseId}/${sectionId}/${topicId}/activity/${exercise}/${memberId}/${createdAt}`;
  return classroomId
    ? `/classrooms/${classroomId}/${attempt}`
    : `/courses/${attempt}`;
};
